'use client';
import React from 'react';
import {
  Facebook, Video, BarChart3, ShieldCheck, MousePointerClick, Zap, ArrowRight, Eye, Lock, MessageCircle,
} from 'lucide-react';
import { useT } from '../i18n';
import { sendTrack } from '../lib/track';

/**
 * 銷售 One-Pager：樓盤 × 社交媒體 一站同步方案（2026-08-24 Captain 委託）
 * 2026-09-16 全文案 i18n 化（promo.* 命名空間，六語）。
 * 素材：樣板影片（uploadfile.jkdcoding.com/demo clipping.mp4）＋Admin 操作實景截圖（/promo-screenshot.jpg）
 */

const DEMO_VIDEO = 'https://uploadfile.jkdcoding.com/demo%20clipping.mp4';

const PromoPage: React.FC = () => {
  const t = useT();
  const trackWa = () => sendTrack({ event: 'whatsapp_click', page: '/promo' });

  return (
    <div className="min-h-screen bg-jkd-black font-sans text-white">
      {/* ---------- ① Hero：影片鉤子 ---------- */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 pt-16 pb-12 md:pt-24">
          <p className="mb-3 inline-block rounded-full border border-jkd-gold/40 px-3 py-1 text-xs font-bold tracking-widest text-jkd-gold">
            {t('promo.kicker')}
          </p>
          <h1 className="text-4xl font-black leading-tight md:text-6xl">
            {t('promo.titlePre')}
            <br />
            <span className="text-jkd-gold">{t('promo.titleHighlight')}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-gray-300 md:text-xl">
            {t('promo.heroLead')}
            <span className="font-bold text-jkd-gold">{t('promo.heroBold')}</span>
            {t('promo.heroTail')}
          </p>

          {/* 樣板影片（自動播放靜音循環——眼球抓取） */}
          <div className="mt-10 overflow-hidden rounded-2xl border border-jkd-gold/30 shadow-2xl shadow-jkd-gold/10">
            <video src={DEMO_VIDEO} autoPlay muted loop playsInline controls={false} className="w-full" />
          </div>
          <p className="mt-3 text-center text-xs text-gray-500">{t('promo.videoCaption')}</p>

          <div className="mt-8 flex flex-wrap gap-4">
            <a onClick={trackWa}
              href={`https://wa.me/85251651480?text=${encodeURIComponent(t('promo.waPrefill'))}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-jkd-gold px-7 py-3.5 text-base font-bold text-jkd-black transition hover:brightness-110"
            >
              <MessageCircle className="h-5 w-5" /> {t('promo.ctaDemo')}
            </a>
            <a
              href="https://jkdcoding.com/knowhow#video-factory"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-gray-600 px-7 py-3.5 text-base font-bold text-gray-200 transition hover:border-jkd-gold/60"
            >
              <Eye className="h-5 w-5" /> {t('promo.ctaTry')}
            </a>
          </div>
        </div>
      </section>

      {/* ---------- ② 痛點 ---------- */}
      <section className="border-t border-white/5 bg-black/40 py-14">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-8 text-center text-2xl font-bold md:text-3xl">{t('promo.painTitle')}</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {[
              { icon: <Zap className="h-7 w-7" />, t: t('promo.pain1.t'), d: t('promo.pain1.d') },
              { icon: <ShieldCheck className="h-7 w-7" />, t: t('promo.pain2.t'), d: t('promo.pain2.d') },
              { icon: <BarChart3 className="h-7 w-7" />, t: t('promo.pain3.t'), d: t('promo.pain3.d') },
            ].map((x) => (
              <div key={x.t} className="rounded-2xl border border-white/10 bg-jkd-black/60 p-6">
                <div className="mb-3 text-red-400">{x.icon}</div>
                <h3 className="mb-2 text-lg font-bold">{x.t}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ③ 三步驟＋操作實景 ---------- */}
      <section className="py-14">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-3 text-center text-2xl font-bold md:text-3xl">
            {t('promo.stepsTitlePre')}<span className="text-jkd-gold">{t('promo.stepsTitleHighlight')}</span>
          </h2>
          <p className="mb-10 text-center text-sm text-gray-500">{t('promo.stepsNote')}</p>

          <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
            <img src="/promo-screenshot.jpg" alt={t('promo.screenshotAlt')} className="w-full" />
          </div>
          <p className="mt-3 mb-10 text-center text-xs text-gray-500">{t('promo.screenshotCaption')}</p>

          <div className="grid gap-5 md:grid-cols-3">
            {[
              { n: '1', icon: <MousePointerClick className="h-6 w-6" />, t: t('promo.step1.t'), d: t('promo.step1.d') },
              { n: '2', icon: <Facebook className="h-6 w-6" />, t: t('promo.step2.t'), d: t('promo.step2.d') },
              { n: '3', icon: <BarChart3 className="h-6 w-6" />, t: t('promo.step3.t'), d: t('promo.step3.d') },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-jkd-gold/25 bg-gradient-to-b from-jkd-gold/10 to-transparent p-6">
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-jkd-gold font-black text-jkd-black">{s.n}</span>
                  <span className="text-jkd-gold">{s.icon}</span>
                </div>
                <h3 className="mb-2 text-lg font-bold">{s.t}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ④ 神器：照片轉影片 ---------- */}
      <section className="relative overflow-hidden border-t border-jkd-gold/20 bg-gradient-to-br from-jkd-black via-[#1a1508] to-jkd-black py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="mb-2 inline-block rounded bg-red-600 px-2 py-0.5 text-xs font-black tracking-widest">{t('promo.magicBadge')}</p>
              <h2 className="text-3xl font-black leading-tight md:text-4xl">
                {t('promo.magicTitlePre')}
                <br />
                <span className="text-jkd-gold">{t('promo.magicTitleHighlight')}</span>
              </h2>
              <p className="mt-4 leading-relaxed text-gray-300">
                {t('promo.magicBodyLead')}
                <span className="font-bold text-white">{t('promo.magicBodyBold')}</span>
                {t('promo.magicBodyTail')}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2"><Video className="h-4 w-4 text-jkd-gold" /> {t('promo.bullet1')}</li>
                <li className="flex items-center gap-2"><Video className="h-4 w-4 text-jkd-gold" /> {t('promo.bullet2')}</li>
                <li className="flex items-center gap-2"><Video className="h-4 w-4 text-jkd-gold" /> {t('promo.bullet3')}</li>
              </ul>
            </div>
            <div className="overflow-hidden rounded-2xl border border-jkd-gold/40 shadow-2xl shadow-jkd-gold/20">
              <video src={DEMO_VIDEO} autoPlay muted loop playsInline controls className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- ⑤ 對比表 ---------- */}
      <section className="border-t border-white/5 bg-black/40 py-14">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="mb-8 text-center text-2xl font-bold md:text-3xl">{t('promo.compareTitle')}</h2>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <table className="w-full text-sm md:text-base">
              <thead>
                <tr className="bg-white/5 text-left">
                  <th className="p-4 font-bold text-gray-300"></th>
                  <th className="p-4 font-bold text-gray-300">{t('promo.compare.col1')}</th>
                  <th className="p-4 font-bold text-jkd-gold">{t('promo.compare.col2')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  [t('promo.compare.r1.c0'), t('promo.compare.r1.c1'), t('promo.compare.r1.c2')],
                  [t('promo.compare.r2.c0'), t('promo.compare.r2.c1'), t('promo.compare.r2.c2')],
                  [t('promo.compare.r3.c0'), t('promo.compare.r3.c1'), t('promo.compare.r3.c2')],
                  [t('promo.compare.r4.c0'), t('promo.compare.r4.c1'), t('promo.compare.r4.c2')],
                  [t('promo.compare.r5.c0'), t('promo.compare.r5.c1'), t('promo.compare.r5.c2')],
                ].map((row) => (
                  <tr key={row[0]}>
                    <td className="p-4 font-semibold text-gray-300">{row[0]}</td>
                    <td className="p-4 text-gray-500 line-through decoration-red-500/60">{row[1]}</td>
                    <td className="p-4 font-bold text-jkd-gold">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-xs text-gray-500">{t('promo.compareNote')}</p>
        </div>
      </section>

      {/* ---------- ⑥ CTA ---------- */}
      <section className="py-16 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="text-3xl font-black md:text-4xl">
            {t('promo.ctaTitlePre')}<span className="text-jkd-gold">{t('promo.ctaTitleHighlight')}</span>
          </h2>
          <p className="mt-4 text-gray-400">{t('promo.ctaSub')}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a onClick={trackWa}
              href={`https://wa.me/85251651480?text=${encodeURIComponent(t('promo.waPrefill'))}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-jkd-gold px-8 py-4 text-lg font-bold text-jkd-black transition hover:brightness-110"
            >
              <MessageCircle className="h-5 w-5" /> {t('promo.ctaDemo')} <ArrowRight className="h-5 w-5" />
            </a>
          </div>
          <p className="mt-6 text-xs text-gray-500">
            <Lock className="mr-1 inline h-3 w-3" /> {t('promo.ctaNote')}
          </p>
        </div>
      </section>

      <footer className="border-t border-white/5 py-6 text-center text-xs text-gray-600">
        {t('promo.footer')} ｜ <a href="https://jkdcoding.com" className="underline hover:text-gray-400">jkdcoding.com</a>
      </footer>
    </div>
  );
};

export default PromoPage;
